// Diagnose Mode: VDR multi-session (Mode 3) only.
// Short-circuits every /vdr/project/* HTTP call against Mode3MockStore when
// `environment.enableMode3Mock` is true (local dev). No-op passthrough otherwise.
// Must be registered AFTER customInterceptor + VDRInterceptor so its wrapped envelope
// still flows through customInterceptor's unwrap step on the way back out.
import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, delay, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Mode3MockStore } from '../services/mode3-mock-store';

interface Envelope<T> {
  status: 'Success' | 'Failure';
  code: number;
  message: string;
  data: T;
}

const ok = <T>(data: T, code = 200): HttpResponse<Envelope<T>> =>
  new HttpResponse({ status: code, body: { status: 'Success', code, message: 'Request succeeded', data } });

const fail = (code: number, message: string): HttpResponse<Envelope<null>> =>
  new HttpResponse({ status: code, body: { status: 'Failure', code, message, data: null } });

const MOCK_LATENCY_MS = 150;

export const mode3MockInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!environment.enableMode3Mock || !req.url.includes('/vdr/project')) {
    return next(req);
  }

  const store = inject(Mode3MockStore);
  // HttpRequest.url is just the path — query params live on req.params. Use urlWithParams
  // so the mock can read `projectId`, `status`, `modifiedBy`, etc. via url.searchParams.
  const url = new URL(req.urlWithParams, 'http://mock.local');
  const path = url.pathname;
  const method = req.method.toUpperCase();

  let response: HttpResponse<unknown>;

  try {
    response = route(store, req, url, path, method);
  } catch (err) {
    response = fail(500, err instanceof Error ? err.message : 'Mock interceptor error');
  }

  // eslint-disable-next-line no-console
  console.info(`[Mode3Mock] ${method} ${path} → ${response.status}`, response.body);

  return of(response).pipe(delay(MOCK_LATENCY_MS));
};

function route(
  store: Mode3MockStore,
  req: HttpRequest<unknown>,
  url: URL,
  path: string,
  method: string,
): HttpResponse<unknown> {
  // POST /vdr/project/initialize
  if (method === 'POST' && path.endsWith('/vdr/project/initialize')) {
    const projectId = store.initializeProject(req.body as any);
    return ok(projectId, 201);
  }

  // GET /vdr/project/psl
  if (method === 'GET' && path.endsWith('/vdr/project/psl')) {
    return ok(store.psls);
  }

  // GET /vdr/project/sessions?projectId&status
  if (method === 'GET' && path.endsWith('/vdr/project/sessions')) {
    const projectId = url.searchParams.get('projectId') ?? '';
    const status = url.searchParams.get('status') ?? undefined;
    const payload = store.getProjectSessions(projectId, status);
    return payload ? ok(payload) : fail(404, `No project exists for the provided projectId [${projectId}].`);
  }

  // PATCH /vdr/project/session/lock
  if (method === 'PATCH' && path.endsWith('/vdr/project/session/lock')) {
    const body = req.body as { sessionId: string; isLocked: boolean };
    const result = store.lockSession(body.sessionId, body.isLocked);
    return result.ok ? ok(null) : fail(409, result.message!);
  }

  // PATCH /vdr/project/{projectId}/psl
  const pslMatch = /\/vdr\/project\/([^/]+)\/psl$/.exec(path);
  if (method === 'PATCH' && pslMatch) {
    const result = store.updateProjectPsl(pslMatch[1], req.body as any);
    return result.ok ? ok(null) : fail(409, result.message!);
  }

  // POST /vdr/project/{projectId}/sessions
  const createMatch = /\/vdr\/project\/([^/]+)\/sessions$/.exec(path);
  if (method === 'POST' && createMatch) {
    const session = store.createSession(createMatch[1], req.body as any);
    return session ? ok(session, 201) : fail(404, `No project exists for the provided projectId [${createMatch[1]}].`);
  }

  // PATCH /vdr/project/session/{sessionId}/rename
  const renameMatch = /\/vdr\/project\/session\/([^/]+)\/rename$/.exec(path);
  if (method === 'PATCH' && renameMatch) {
    const session = store.renameSession(renameMatch[1], req.body as any);
    return session ? ok(session) : fail(409, `Session [${renameMatch[1]}] could not be renamed.`);
  }

  // DELETE /vdr/project/session/{sessionId}?modifiedBy=
  const deleteMatch = /\/vdr\/project\/session\/([^/]+)$/.exec(path);
  if (method === 'DELETE' && deleteMatch) {
    const result = store.deleteSession(deleteMatch[1]);
    return result.ok ? ok(null) : fail(409, result.message!);
  }

  return fail(404, `Mode 3 mock has no route for ${method} ${path}`);
}
